// All shared styles + UI primitives. No CSS framework -- inline style objects only.
import { useEffect, useState } from 'react';

export const colors = {
  charcoal: '#1F2937',
  charcoalSoft: '#374151',
  orange: '#E36F2C',
  orangeDark: '#C45A1F',
  bg: '#F8FAFC',
  card: '#FFFFFF',
  border: '#E5E7EB',
  text: '#111827',
  textMuted: '#6B7280',
  green: '#10B981',
  greenSoft: '#D1FAE5',
  red: '#EF4444',
  redSoft: '#FEE2E2',
  amber: '#F59E0B',
  amberSoft: '#FEF3C7',
  blue: '#2563EB',
  blueSoft: '#DBEAFE',
  gray: '#9CA3AF',
  graySoft: '#F3F4F6'
};

// --- Mobile detection ---------------------------------------------------
// Phone-only fallback. Above this width the desktop layout is unchanged.
export const MOBILE_BREAKPOINT = 720;

export const useIsMobile = (breakpoint = MOBILE_BREAKPOINT) => {
  const query = `(max-width: ${breakpoint}px)`;
  const [is, setIs] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setIs(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return is;
};

// Mobile-only style overrides. Spread onto a base style only when
// useIsMobile() returns true; desktop never sees these.
export const mobile = {
  // overflowX auto is the page-width guard: any element wider than the phone
  // (dense admin tables, Estimating grids) scrolls sideways INSIDE the
  // container instead of stretching the document. Without it, iOS zooms out
  // to fit the widest element AND keeps that zoom across SPA navigation, so
  // narrow pages visited afterward render "squished for no reason".
  // NO WebkitOverflowScrolling here: on iOS it creates a stacking context on
  // the container, which traps every modal's z-index UNDER the sticky topbar
  // (bright undimmed bar floating over the Job Hub — George 2026-07-07).
  // Modern iOS momentum-scrolls by default; the property is legacy.
  container: { padding: '12px 14px', overflowX: 'auto' },
  // Sticky on mobile (George 2026-07-07, matching sss-inspection): the
  // hamburger + bell stay reachable while scrolling long lists instead of
  // requiring a scroll back to the top. The drawer is position:absolute
  // inside the bar, so it rides along under it.
  // flexWrap: under Safari per-site page zoom the CSS viewport can shrink to
  // ~300px; the role chip then wraps below the logo instead of clipping off
  // the right edge of the screen. No effect at normal widths.
  topbar: { padding: '10px 14px', position: 'sticky', top: 0, zIndex: 40, flexWrap: 'wrap', rowGap: 6 },
  hamburger: {
    background: 'transparent', border: 'none', color: '#fff',
    fontSize: 24, cursor: 'pointer', padding: '4px 10px', lineHeight: 1
  },
  drawer: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: colors.charcoal, display: 'flex', flexDirection: 'column',
    padding: '4px 0', zIndex: 30, boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
    // The topbar is sticky, so the drawer stays pinned while the page
    // scrolls behind it — without its own scroll, links below the fold
    // (Estimating, Admin) were unreachable on zoomed/short screens.
    maxHeight: '70vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch'
  },
  drawerLink: {
    // display:block is load-bearing: top-level drawer links stack anyway
    // (direct flex children get blockified), but the Sales GROUP links sit
    // inside a wrapper div, so without it they render as inline <a>s flowing
    // horizontally out of the drawer.
    display: 'block', color: '#E5E7EB', padding: '14px 18px', textDecoration: 'none',
    fontSize: 15, fontWeight: 500, borderTop: `1px solid ${colors.charcoalSoft}`
  },
  drawerLinkActive: { background: colors.orange, color: '#fff' },
  // --- Card-list primitives for replacing tables on phones ---
  listItem: {
    background: colors.card, border: `1px solid ${colors.border}`,
    borderRadius: 8, padding: 14, marginBottom: 10
  },
  listItemHeader: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 12, marginBottom: 8
  },
  listItemTitle: { fontSize: 16, fontWeight: 700, color: colors.charcoal, lineHeight: 1.3 },
  listRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: 12, padding: '6px 0', fontSize: 13, borderTop: `1px solid ${colors.border}`
  },
  listLabel: {
    color: colors.textMuted, fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0
  },
  listValue: { color: colors.text, fontSize: 14, fontWeight: 500, textAlign: 'right' },
  listActions: {
    display: 'flex', gap: 8, marginTop: 12, paddingTop: 12, flexWrap: 'wrap',
    borderTop: `1px solid ${colors.border}`
  },
  btnTap: { padding: '10px 16px', fontSize: 14, minHeight: 44 },
  emptyHint: { textAlign: 'center', color: colors.textMuted, padding: '24px 12px', fontSize: 14 }
};

const BTN_SHADOW = '0 1px 2px rgba(17, 24, 39, 0.12), 0 2px 4px rgba(17, 24, 39, 0.06)';

export const styles = {
  page: { minHeight: '100vh', background: colors.bg, color: colors.text, fontSize: 14 },
  container: { maxWidth: 1400, margin: '0 auto', padding: '24px 32px' },
  topbar: {
    background: colors.charcoal, color: '#fff', padding: '12px 32px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
  },
  brand: { fontWeight: 700, letterSpacing: 1, color: colors.orange, fontSize: 18 },
  navLink: {
    color: '#E5E7EB', padding: '8px 14px', borderRadius: 6,
    textDecoration: 'none', fontWeight: 500, fontSize: 13
  },
  navLinkActive: { background: colors.orange, color: '#fff' },
  card: {
    background: colors.card, border: `1px solid ${colors.border}`,
    borderRadius: 8, padding: 20, marginBottom: 16
  },
  h1: { fontSize: 22, fontWeight: 700, margin: '0 0 16px 0', color: colors.charcoal },
  h2: { fontSize: 16, fontWeight: 600, margin: '0 0 12px 0', color: colors.charcoal },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 4 },
  input: {
    width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`,
    borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
    background: '#fff'
  },
  textarea: {
    width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`,
    borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
    minHeight: 80, resize: 'vertical', background: '#fff'
  },
  // Buttons read as raised buttons (George 2026-11-01): a filled face, a defined
  // border and a drop shadow. tabBtn / tabBtnActive are for tab bars and toggles,
  // where the selected one is orange and pressed in.
  btn: {
    background: colors.orange, color: '#fff', border: `1px solid ${colors.orangeDark}`,
    padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
    fontSize: 13, boxShadow: BTN_SHADOW
  },
  btnSecondary: {
    background: 'linear-gradient(#FFFFFF, #F3F4F6)', color: colors.charcoal, border: '1px solid #C3C8D0',
    padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
    fontSize: 13, minWidth: 70, textAlign: 'center', boxShadow: BTN_SHADOW
  },
  btnDanger: {
    background: 'linear-gradient(#FFFFFF, #FEF2F2)', color: colors.red, border: `1px solid ${colors.red}`,
    padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
    fontSize: 13, minWidth: 70, textAlign: 'center', boxShadow: BTN_SHADOW
  },
  tabBtn: {
    background: 'linear-gradient(#FFFFFF, #F3F4F6)', color: colors.charcoal, border: '1px solid #C3C8D0',
    padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
    fontSize: 13, minWidth: 70, textAlign: 'center', boxShadow: BTN_SHADOW
  },
  tabBtnActive: {
    background: colors.orange, color: '#fff', border: `1px solid ${colors.orangeDark}`,
    padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
    fontSize: 13, minWidth: 70, textAlign: 'center', boxShadow: 'inset 0 2px 3px rgba(0, 0, 0, 0.22)'
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '10px 12px', borderBottom: `2px solid ${colors.border}`,
    color: colors.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  td: { padding: '10px 12px', borderBottom: `1px solid ${colors.border}`, verticalAlign: 'top' },
  pill: {
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
    // Fixed width so every status pill is the same size in tables.
    minWidth: 70, textAlign: 'center', boxSizing: 'border-box'
  },
  kpiGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16, marginBottom: 24
  },
  kpiCard: {
    background: colors.card, border: `1px solid ${colors.border}`,
    borderRadius: 8, padding: 18
  },
  kpiLabel: { fontSize: 11, color: colors.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 },
  kpiValue: { fontSize: 26, fontWeight: 700, color: colors.charcoal, marginTop: 6 },
  kpiSubtext: { fontSize: 12, color: colors.textMuted, marginTop: 4 },
  formGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 12, marginBottom: 16
  },
  errorBox: {
    background: colors.redSoft, color: colors.red, padding: '10px 14px',
    borderRadius: 6, marginBottom: 12, fontSize: 13
  }
};

// Shared pill across the app. Carries both the Activity Log statuses
// (In the Hunt / Bidding / Won / Lost / Closed) and the Project Tracker
// statuses (Bidding / Active / Won / Lost / Complete). They share some
// values (Bidding / Won / Lost) which is fine -- the visual is the same.
// A clickable pill (filter / toggle chip) that reads as a raised button (George
// 2026-11-01): its own colors, a visible edge and a drop shadow; the selected one
// is outlined in its text color and pressed in.
export const chipBtn = (bg, fg, active) => ({
  ...styles.pill, padding: '4px 12px', cursor: 'pointer', background: bg, color: fg,
  border: `1px solid ${active ? fg : 'rgba(17, 24, 39, 0.18)'}`,
  boxShadow: active ? 'inset 0 2px 3px rgba(0, 0, 0, 0.18)' : BTN_SHADOW,
});

export const StatusPill = ({ status }) => {
  const map = {
    'In the Hunt': { bg: '#EDE9FE', fg: '#5B21B6' },  // indigo -- prospecting
    'Bidding':     { bg: colors.amberSoft, fg: colors.amber }, // amber -- actively quoting
    'Mobilization Standby': { bg: '#CFFAFE', fg: '#0E7490' }, // cyan -- not won yet, be ready to mobilize (projects only)
    'Active':      { bg: colors.blueSoft,  fg: colors.blue },  // blue -- in progress (projects only)
    'Won':         { bg: colors.greenSoft, fg: colors.green },
    'Lost':        { bg: colors.redSoft,   fg: colors.red },
    'Complete':    { bg: colors.graySoft,  fg: colors.gray },  // projects only
    'Closed':      { bg: colors.graySoft,  fg: colors.gray }   // activity only -- terminal
  };
  const s = map[status] || { bg: colors.graySoft, fg: colors.gray };
  return <span style={{ ...styles.pill, background: s.bg, color: s.fg }}>{status}</span>;
};

export const fmtMoney = (v) => {
  const n = Number(v || 0);
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

export const fmtMoneyK = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1_000) return '$' + (n / 1_000).toFixed(0) + 'K';
  return '$' + n.toFixed(0);
};

export const fmtPct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

// Date formatter for date-only values (project date_planned, follow-up dates,
// week_of, etc.). Parses the YYYY-MM-DD prefix as local-time components so
// the display year matches the stored year. Without this, a stored
// "2026-01-01" gets interpreted as UTC midnight and rendered as
// "Dec 31, 2025 18:00" in CST -- looks like the wrong year on the page.
export const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d).slice(0, 10); // first 10 chars: YYYY-MM-DD
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    const dt = new Date(d);
    return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])); // local time
  // The (y, m, d) constructor maps years 0-99 to 1900+y, so a stored typo
  // year like 0026 rendered as "1926". Show the year that's actually stored;
  // the server now normalizes 2-digit years to 20xx on write anyway.
  dt.setFullYear(Number(m[1]));
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const fmtDateTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return dt.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// ISO Monday string for the current week
export const currentWeekOf = () => {
  const d = new Date();
  const day = d.getDay() || 7;
  if (day !== 1) d.setDate(d.getDate() - (day - 1));
  return d.toISOString().slice(0, 10);
};

// Year options for filter dropdowns. Defaults to current year - 1 .. + 2
// so reps see prior year (closing out) plus this year and next two (planning).
export const yearOptions = () => {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1, y + 2];
};
export const currentYear = () => new Date().getFullYear();

// Display roles with proper capitalization (DB stores them lowercase to keep
// SQL comparisons consistent). Sales reps are referred to as "Account
// Manager" in the UI per company convention.
const ROLE_DISPLAY = {
  rep: 'Account Manager',
  supervisor: 'Supervisor',
  leadership: 'Leadership',
  admin: 'Admin',
  ops: 'Ops'
};
export const fmtRole = (r) => r ? (ROLE_DISPLAY[r] || (r.charAt(0).toUpperCase() + r.slice(1))) : '';

// Requests were historically seeded with the literal title "New Request",
// which then stuck forever (showing "New Request" on Estimated/Submitted rows).
// Treat that placeholder — and any empty value — as "no real title". Returns
// the cleaned title, or '' when it's a placeholder; callers append
// `|| '(untitled)'` for display and use the bare value for edit inputs.
export const reqDisplayTitle = (t) => {
  const s = (t || '').trim();
  return s.toLowerCase() === 'new request' ? '' : s;
};

export const Modal = ({ title, onClose, children }) => (
  <div onClick={onClose} style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50
  }}>
    <div onClick={e => e.stopPropagation()} style={{
      background: '#fff', borderRadius: 10, width: 480, maxWidth: '92vw',
      // Height cap + own scroll: without it, content taller than the screen
      // (zoomed phones especially) overflowed with no way to scroll.
      maxHeight: '88vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      padding: 24, boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>{title}</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: colors.textMuted }}>×</button>
      </div>
      {children}
    </div>
  </div>
);
